import type { LinkingOptions, PathConfigMap } from '@react-navigation/native';
import { getStateFromPath as defaultGetStateFromPath } from '@react-navigation/native';
import { getAuthStore } from '../shared/auth';
import {
  buildDeepLinkScreenMaps,
  getAuthOnlyDeepLinkPrefixes,
} from '../shared/navigation/screenRegistry';
import { getTabDeepLinkPaths } from '../shared/navigation/tabRegistry';
import type { RootStackParamList } from './types';

function buildAuthLinkingScreens(): PathConfigMap<RootStackParamList> {
  const { auth_screens } = buildDeepLinkScreenMaps();
  return auth_screens as PathConfigMap<RootStackParamList>;
}

function buildMainLinkingScreens(): PathConfigMap<RootStackParamList> {
  const { main_screens } = buildDeepLinkScreenMaps();
  const tab_screens = getTabDeepLinkPaths();

  return {
    MainTabs: {
      path: '',
      screens: tab_screens as PathConfigMap<RootStackParamList>,
    },
    ...main_screens,
  } as PathConfigMap<RootStackParamList>;
}

function normalizeDeepLinkPath(path: string): string {
  return path.replace(/^\/+/, '').split('?')[0]?.split('#')[0] ?? '';
}

function isAuthOnlyDeepLinkPath(path: string): boolean {
  const normalized = normalizeDeepLinkPath(path);
  if (!normalized) {
    return false;
  }
  const prefixes = getAuthOnlyDeepLinkPrefixes();
  return prefixes.some(prefix => normalized === prefix || normalized.startsWith(`${prefix}/`));
}

/**
 * Universal / app links. App swaps Auth vs Main stack (legacy model); paths resolve against the active stack.
 */
export const navigationLinking: LinkingOptions<RootStackParamList> = {
  prefixes: ['eataami://'],
  config: {
    screens: buildMainLinkingScreens(),
  },
  getStateFromPath(path, options) {
    const is_authenticated = getAuthStore().isAuthenticated;
    const auth_linking_screens = buildAuthLinkingScreens();
    const main_linking_screens = buildMainLinkingScreens();

    if (!is_authenticated && isAuthOnlyDeepLinkPath(path)) {
      return defaultGetStateFromPath('', {
        ...options,
        screens: auth_linking_screens,
      });
    }

    const leaf_screens = is_authenticated ? main_linking_screens : auth_linking_screens;
    return defaultGetStateFromPath(path, {
      ...options,
      screens: leaf_screens,
    });
  },
};
